/**
 * Soft-tissue springs — ADR-038 J2: **a leaf's spring, declared through the
 * VRMC_springBone door that exists on main, and the SL-style region surface
 * recorded beside it.**
 *
 * ONE responsibility: turn a grafted leaf (its bone, tip, flesh radius) and a
 * region's CONTROLS into (a) a {@link SpringDeclaration} the reference solver
 * runs today and every VRM reader understands, with colliders fitted to THIS
 * body, and (b) the controls themselves, which ride the spring's
 * `extras.poqpoq.jiggle` in the file.
 *
 * THE SURFACE (research: Second Life avatar physics; PhysBones; ADR-038 R8):
 * per region — on/off, bounce, sway, spread (cleavage for a breast, the
 * glutes' spread), mass, max effect, gravity + gravity falloff, stiffness,
 * drag. SL as UX precedent only: no SL constants or assets.
 *
 * WHAT IS LIVE (J3, 2026-09-27). A VRMC_springBone joint is ONE isotropic
 * spring: a stiffness pulling the tip back along its rest axis, a drag, a
 * gravity. `on`, `stiffness`, `drag`, `gravity` and `mass` become the joint's
 * numbers (the joint's stiffness is `stiffness / mass`: a heavier mass is a
 * slower spring, ω² = k/m), so any VRM reader plays them. `bounce`, `sway`,
 * `maxEffect` and `gravityFalloff` are LIVE in Harry's viewport through the
 * layered drive ({@link softTissueDrive}): the animation is the leaf's target
 * (a flexed pec or a keyed breast is followed, not overwritten), the cone is
 * max effect, gravity is gated by displacement, and bounce / sway scale the
 * shown deviation per axis. World ports the same arithmetic. `spread` stays
 * RECORDED: cleavage is the pair's DIFFERENTIAL lateral motion (the two
 * breasts toward / away from each other), which two independent leaves
 * cannot tell from sway — it needs the pair coupled. It round-trips in the
 * file, and the card marks it recorded.
 *
 * THE COLLIDERS (fitted, clipped so nothing starts in penetration):
 *  - breast: a CHEST sphere on the chest bone, centred on the spine line at
 *    the root's height, radius reaching just short of the root (the breast
 *    cannot swing into the ribcage); the two UPPER-ARM capsules (never through
 *    the arms); the OTHER breast's tip as a sphere (never through each other);
 *  - glute: the OTHER thigh's capsule (its own thigh would kick it every
 *    stride) and the other glute's tip;
 *  - belly: the two thigh capsules (a seated or high-kneed pose).
 *  Each collider is shrunk until the region's tip, with its hit radius and a
 *  5 mm margin, starts outside it; one shrunk below 1 cm is dropped. Both are
 *  reported.
 *
 * Depends on: zod, springs/spring-declaration (the carrier, read only),
 * jiggle/regions, ir/rig-descriptor, math. Depended on by: the app's jiggle
 * controller, the specimen probe, the round trip.
 */
import { z } from 'zod';
import {
  springDeclarationSchema,
  type SpringCollider,
  type SpringDeclaration,
} from '../springs/spring-declaration.js';
import type { RigDescriptor } from '../ir/rig-descriptor.js';
import { quatInvertUnit, quatRotateVec3 } from '../math/quat.js';
import type { Vec3Data } from '../math/vec3.js';
import { JIGGLE_REGIONS, kindOfRegion, type JiggleKind, type JiggleRegionId } from './regions.js';
import { SOFT_TISSUE_SLOT } from './leaf-bones.js';
import { TAIL_DYNAMICS_DEFAULTS, type SpringDynamics } from '../springs/spring-drive.js';

const finite = z.number().finite();

/** The region surface — SL-style axes over a VRM spring (see the header for what is live). */
export const softTissueControlsSchema = z.object({
  on: z.boolean(),
  /** LIVE: VRM stiffnessForce — how hard the mass returns to its shape. */
  stiffness: finite.min(0).max(4),
  /** LIVE: VRM dragForce — the share of the swing lost each step. */
  drag: finite.min(0).max(1),
  /** LIVE: VRM gravityPower (down). */
  gravity: finite.min(0).max(1),
  /** LIVE (viewport): vertical response gain, 0–3 (SL Bounce) — 1 = the physics as solved, 3 = triple it. */
  bounce: finite.min(0).max(3),
  /** LIVE (viewport): lateral response gain, 0–3 (SL Sway). */
  sway: finite.min(0).max(3),
  /** RECORDED: in/out response gain, 0–1 (SL Cleavage; the glutes' spread) — the pair's differential motion, not yet coupled. */
  spread: finite.min(0).max(1),
  /** LIVE: the mass the stiffness divides by (SL Mass) — the joint declares `stiffness / mass`. */
  mass: finite.positive().max(10),
  /** LIVE (viewport): the largest swing, degrees — the drive's cone (SL Max Effect; 0 = off, no cone). */
  maxEffectDeg: finite.min(0).max(90),
  /** LIVE (viewport): 1 = gravity acts only when displaced from rest (PhysBones Gravity Falloff), scaled by the share of max effect. */
  gravityFalloff: finite.min(0).max(1),
  /**
   * LIVE (viewport, ADR-038 §14 J4): the SURFACE jiggle's gain, 0–3 — how hard the bounce rings the
   * mass's membrane modes (0 = off). Absent in files and Documents written before J4: the default.
   */
  jiggle: finite.min(0).max(3).default(2),
});
export type SoftTissueControls = z.infer<typeof softTissueControlsSchema>;

/** The controls Harry's viewport and a VRM reader play today; the rest are recorded. */
export const SOFT_TISSUE_LIVE_CONTROLS: readonly (keyof SoftTissueControls)[] = [
  'on',
  'stiffness',
  'drag',
  'gravity',
  'mass',
  'bounce',
  'sway',
  'maxEffectDeg',
  'gravityFalloff',
  'jiggle',
];

/** The joint's declared stiffness for a region's controls: `stiffness / mass` (ω² = k/m). */
export const softTissueJointStiffness = (c: Pick<SoftTissueControls, 'stiffness' | 'mass'>): number => c.stiffness / c.mass;

/**
 * The layered drive's dynamics for a leaf (ADR-038 J3). The tail's defaults
 * with three changes: the cone is max effect (0 = no cone); the leaf inherits
 * ALL of the body's motion (inertia 1 — the reference joint's own behaviour,
 * the lag that IS the jiggle; the tail's 0.3 carries most of it along); no
 * reflex. The speed caps and teleport reset stay: a cut or a loop's root jump
 * must not fling a breast.
 */
export function softTissueDynamics(c: Pick<SoftTissueControls, 'maxEffectDeg'>): SpringDynamics {
  return {
    ...TAIL_DYNAMICS_DEFAULTS,
    coneDeg: c.maxEffectDeg > 0 ? c.maxEffectDeg : 180,
    inertia: 1,
    reflex: { ...TAIL_DYNAMICS_DEFAULTS.reflex, gainS: 0 },
  };
}

/**
 * The defaults per region. A starting point for Allen's eye (ADR-038 R4),
 * chosen from a sweep in the specimen probe (`JIGGLE_SWEEP`, the tiger
 * warrior's 2–3 cm leaves under the Mixamo walk and a 12 cm three-hop,
 * 2026-09-24). The reference solver's stiffness is a displacement per second
 * at the TIP, so a short leaf is stiff in angle: the breast at 0.35 / drag 0.2
 * swung 16° in the walk and 51° in the hop (the mass flipping over); at
 * 0.8 / 0.3, 8.6° and 31° (tip travel 0.55 / 1.8 cm), back within 1° of rest
 * 0.08 s after landing; at 1.5 / 0.35, 5° and 19°. Belly and glute sit a
 * step stiffer (the walk already swings a belly leaf ~6° with the pelvis's
 * own twist at any stiffness). Gravity is small (0.01 sagged a breast 0.7°
 * before J3's falloff zeroed the rest sag).
 *
 * BOUNCE / SWAY (J3, 2026-09-27) are OUTPUT gains on the shown deviation, so
 * 1 = the physics as solved and anything less only shrinks it. J2's recorded
 * 0.6 / 0.4 halved every swing once they went live (tiger-woman's breast hop
 * 27.7° → 12°, pinned at 0.6 × the 20° cone), so the vertical axis is 1 and
 * sway a step below: the mass rises and falls freely and swings sideways less.
 */
export const SOFT_TISSUE_DEFAULTS: Readonly<Record<JiggleKind, SoftTissueControls>> = {
  breast: { on: true, stiffness: 0.8, drag: 0.08, gravity: 0.01, bounce: 1, sway: 0.7, spread: 0.3, mass: 1, maxEffectDeg: 20, gravityFalloff: 1, jiggle: 2 },
  belly: { on: true, stiffness: 1.5, drag: 0.12, gravity: 0.005, bounce: 0.9, sway: 0.5, spread: 0, mass: 1, maxEffectDeg: 12, gravityFalloff: 1, jiggle: 2 },
  glute: { on: true, stiffness: 1.2, drag: 0.1, gravity: 0.005, bounce: 1, sway: 0.6, spread: 0.2, mass: 1, maxEffectDeg: 15, gravityFalloff: 1, jiggle: 2 },
  /**
   * ATTACHED HAIR (ADR-039 T2a, Allen 2026-09-30: softening, not free motion).
   * Stiff — 2.5 is three times the breast's 0.8, past the Firm preset's rate
   * (the ring sweep's table tops out at ≈ 5.6 Hz), on a leaf twice a breast's
   * length (skull centre → the hair's weighted centroid, 6–11 cm on the
   * specimens) — a short, quick ring (drag 0.15 ≈ 0.2 s) inside a SMALL cone
   * (6°: a few millimetres at the hair's surface once the share scales it).
   * NO gravity: attached hair holds its sculpt (§3.3; gravity is T3's, for
   * chains). Spread is meaningless for one leaf (0).
   * LOOSENED 2026-10-01 (Allen's first field read on black lace #6: "give it a bit more
   * jiggle … better to take it too far and come back"): Natural's breast rate (0.8,
   * ≈ 4.3 Hz) ringing ~3 cycles (drag 0.07), a 15° cone. Was 2.5 / 0.15 / 6°. Jiggle went to 3 (the control's top) and was "super jiggle" —
   * then Bounce / Sway / Jiggle sliders on the card; Allen settled on 1 / 0.8 / 0.4 (HAIR_MOTION_DEFAULT).
   */
  hair: { on: true, stiffness: 0.8, drag: 0.07, gravity: 0, bounce: 1, sway: 0.8, spread: 0, mass: 1, maxEffectDeg: 15, gravityFalloff: 1, jiggle: 0.4 },
};

/**
 * DRAG (the ring sweep, 2026-09-27 — `ring-sweep-probe.test.ts`, Stella and the
 * tiger-woman's left breast, J3 drive, one feedback kick on a still body):
 * FIRMNESS sets the RATE (0.4 → ~3 Hz, 0.8 → ~4.3 Hz, 1.5 → ~5.6 Hz; drag
 * barely moves it) and DRAG sets how long it RINGS — it is the share of the
 * velocity lost EVERY 60 Hz step, so J2's 0.3 kept 70% a step and died in one
 * visible cycle (0.2 s); 0.08 rings three cycles (~0.4 s), 0.05 four (~0.75 s).
 * The high drag guarded against a hop flipping the mass over; Max effect (a
 * SOFT cone, `softCone`) now does that job, so the defaults ring.
 */

/** The novice's presets: one click sets rate, ring, weight and reach together. */
export const SOFT_TISSUE_PRESETS = ['firm', 'natural', 'bouncy', 'heavy'] as const;
export type SoftTissuePreset = (typeof SOFT_TISSUE_PRESETS)[number];

/**
 * A preset's controls for a region kind (measured on breasts; belly and glute
 * sit a step firmer, as their defaults do). Firm ≈ 5.6 Hz, 2 cycles, 0.2 s;
 * Natural ≈ 4.3 Hz, 3 cycles, 0.4 s (the default); Bouncy ≈ 3.1 Hz, 4 cycles,
 * 0.75 s; Heavy = Bouncy's spring with twice the mass (half the joint
 * stiffness — slower) and a gravity that sags it while it swings.
 */
export function softTissuePreset(kind: JiggleKind, preset: SoftTissuePreset): SoftTissueControls {
  const base = SOFT_TISSUE_DEFAULTS[kind];
  const step = kind === 'breast' ? 1 : kind === 'glute' ? 1.5 : kind === 'hair' ? 1 : 1.9;
  switch (preset) {
    case 'firm':
      return { ...base, stiffness: 1.5 * step, drag: 0.12, mass: 1, gravity: base.gravity, maxEffectDeg: base.maxEffectDeg };
    case 'natural':
      return { ...base };
    case 'bouncy':
      return { ...base, stiffness: 0.4 * step, drag: 0.05, mass: 1, bounce: Math.max(base.bounce, 1.2), maxEffectDeg: base.maxEffectDeg + 5 };
    case 'heavy':
      return { ...base, stiffness: 0.4 * step, drag: 0.08, mass: 2, gravity: 0.05, bounce: Math.max(base.bounce, 1.2), maxEffectDeg: base.maxEffectDeg + 10 };
  }
}

/** The spring's name in the file: `jiggle-LeftBreast`. */
export const softTissueSpringName = (region: JiggleRegionId): string => `jiggle-${region}`;
/** Is this declaration a soft-tissue spring (by its name in the file)? */
export const isSoftTissueSpring = (decl: Pick<SpringDeclaration, 'name'>): boolean => /^jiggle-/.test(decl.name);
/** The region a soft-tissue spring drives, or null. */
export function softTissueRegionOfSpring(decl: Pick<SpringDeclaration, 'name'>): JiggleRegionId | null {
  const id = decl.name.replace(/^jiggle-/, '');
  return (JIGGLE_REGIONS as readonly string[]).includes(id) && isSoftTissueSpring(decl) ? (id as JiggleRegionId) : null;
}

/** What the file carries beside the spring (`extras.poqpoq.jiggle`). */
export const softTissueExtrasSchema = z.object({
  region: z.enum(JIGGLE_REGIONS),
  controls: softTissueControlsSchema,
  /** Which controls a VRM reader plays today — so a reader knows what the rest are waiting for. */
  live: z.array(z.string()),
});
export type SoftTissueExtras = z.infer<typeof softTissueExtrasSchema>;

export function softTissueExtras(region: JiggleRegionId, controls: SoftTissueControls): SoftTissueExtras {
  return softTissueExtrasSchema.parse({ region, controls, live: [...SOFT_TISSUE_LIVE_CONTROLS] });
}

// ---------------------------------------------------------------------------
// Colliders
// ---------------------------------------------------------------------------

/** One leaf as the spring sees it: names the file speaks, bind-world head/tip, flesh radius. */
export interface SoftTissueLeafInput {
  readonly region: JiggleRegionId;
  /** The rig names (`…_graft` in a session; the file's names on a reopen). */
  readonly bone: string;
  readonly tip: string;
  readonly head: Vec3Data;
  readonly tail: Vec3Data;
  /** The region's flesh radius (world units). */
  readonly radius: number;
}

export interface SoftTissueColliderFit {
  readonly bone: string;
  readonly kind: 'sphere' | 'capsule';
  readonly what: string;
  readonly fleshRadius: number;
  readonly radius: number;
  readonly clipped: boolean;
  readonly dropped: boolean;
}

export interface SoftTissueSpringInput {
  readonly leaf: SoftTissueLeafInput;
  /** Every leaf of the body (the other breast / glute is a collider). */
  readonly others: readonly SoftTissueLeafInput[];
  readonly rig: RigDescriptor;
  /** Flesh radius of a limb segment by its PARENT rig bone (upper arm, thigh), measured by the caller; absent = not fitted. */
  readonly limbRadius: (bone: string) => number | undefined;
  readonly controls?: SoftTissueControls;
  /** The names the FILE speaks (`_graft` stripped) — the declaration's vocabulary. */
  readonly fileName?: (name: string) => string;
}

export interface SoftTissueSpring {
  readonly decl: SpringDeclaration;
  readonly extras: SoftTissueExtras;
  readonly fitted: readonly SoftTissueColliderFit[];
  /** The tip's hit radius (world units). */
  readonly hitRadius: number;
}

const MARGIN = 0.005;
const MIN_RADIUS = 0.01;
/** The tip's hit sphere: this share of the region's flesh radius. */
export const SOFT_TISSUE_HIT_SHARE = 0.35;

const sub = (a: Vec3Data, b: Vec3Data): Vec3Data => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (a: Vec3Data): number => Math.hypot(a.x, a.y, a.z);
function distanceToSegment(p: Vec3Data, a: Vec3Data, b: Vec3Data): number {
  const ab = sub(b, a);
  const l2 = ab.x * ab.x + ab.y * ab.y + ab.z * ab.z;
  const ap = sub(p, a);
  const t = l2 > 0 ? Math.min(1, Math.max(0, (ap.x * ab.x + ap.y * ab.y + ap.z * ab.z) / l2)) : 0;
  return len({ x: ap.x - t * ab.x, y: ap.y - t * ab.y, z: ap.z - t * ab.z });
}

/** The soft-tissue spring for one leaf: the prior's numbers, the fitted colliders, the recorded surface. */
export function softTissueSpring(input: SoftTissueSpringInput): SoftTissueSpring {
  const { leaf, rig } = input;
  const kind = kindOfRegion(leaf.region);
  const controls = softTissueControlsSchema.parse(input.controls ?? SOFT_TISSUE_DEFAULTS[kind]);
  const fileName = input.fileName ?? ((n: string) => n.replace(/_graft$/, ''));
  const boneOf = (name: string) => rig.bones.find((b) => b.name === name);
  const mapped = (id: string): string | undefined => rig.mapping.find((m) => m.ontologyId === id)?.rigBone;
  /** A world point in a bone's LOCAL frame (the spec's collider offsets). */
  const local = (bone: string, p: Vec3Data): [number, number, number] => {
    const b = boneOf(bone)!;
    const r = quatRotateVec3(quatInvertUnit(b.worldBind.rotation), sub(p, b.worldBind.position));
    const s = b.worldBind.scale;
    return [r.x / s.x, r.y / s.y, r.z / s.z];
  };
  const hitRadius = Math.max(0.005, SOFT_TISSUE_HIT_SHARE * leaf.radius);
  const tip = leaf.tail;
  const fitted: SoftTissueColliderFit[] = [];
  const colliders: SpringCollider[] = [];
  /** Shrink until the tip (with its hit sphere and a margin) is clear; drop below 1 cm. */
  const place = (bone: string, what: string, kind2: 'sphere' | 'capsule', flesh: number, dist: (r: number) => number, shape: (r: number) => SpringCollider['shape']): void => {
    let r = flesh;
    const clear = dist(0) - hitRadius - MARGIN;
    const clipped = r > clear;
    if (clipped) r = Math.max(0, clear);
    const dropped = r < MIN_RADIUS;
    fitted.push({ bone: fileName(bone), kind: kind2, what, fleshRadius: flesh, radius: dropped ? 0 : r, clipped, dropped });
    if (!dropped) colliders.push({ bone: fileName(bone), shape: shape(r) });
  };
  const capsuleFor = (upper: string | undefined, lower: string | undefined, what: string): void => {
    if (upper === undefined || lower === undefined || boneOf(upper) === undefined || boneOf(lower) === undefined) return;
    const flesh = input.limbRadius(upper);
    if (flesh === undefined || !(flesh > 0)) return;
    const a = boneOf(upper)!.worldBind.position;
    const b = boneOf(lower)!.worldBind.position;
    place(upper, what, 'capsule', flesh, () => distanceToSegment(tip, a, b), (r) => ({ capsule: { offset: [0, 0, 0], radius: r, tail: local(upper, b) } }));
  };
  const otherTipSphere = (other: SoftTissueLeafInput, what: string): void => {
    const flesh = Math.max(0.005, SOFT_TISSUE_HIT_SHARE * other.radius);
    place(other.bone, what, 'sphere', flesh, () => len(sub(tip, other.tail)), (r) => ({ sphere: { offset: local(other.bone, other.tail), radius: r } }));
  };
  if (kind === 'breast') {
    const chest = mapped('Spine2') ?? mapped('Spine1') ?? mapped('Spine');
    if (chest !== undefined && boneOf(chest) !== undefined) {
      const c = boneOf(chest)!.worldBind.position;
      // The chest sphere's centre: the spine line at the ROOT's height, so its
      // surface sits just behind the root — the breast pivots on it, never into it.
      const centre = { x: c.x, y: leaf.head.y, z: c.z };
      const flesh = Math.max(0, len(sub(leaf.head, centre)) - MARGIN);
      place(chest, 'the ribcage', 'sphere', flesh, () => len(sub(tip, centre)), (r) => ({ sphere: { offset: local(chest, centre), radius: r } }));
    }
    for (const side of ['Left', 'Right'] as const) capsuleFor(mapped(`${side}Arm`), mapped(`${side}ForeArm`), `the ${side.toLowerCase()} upper arm`);
    const twin = input.others.find((o) => o.region === (leaf.region === 'LeftBreast' ? 'RightBreast' : 'LeftBreast'));
    if (twin !== undefined) otherTipSphere(twin, 'the other breast');
  } else if (kind === 'belly') {
    for (const side of ['Left', 'Right'] as const) capsuleFor(mapped(`${side}UpLeg`), mapped(`${side}Leg`), `the ${side.toLowerCase()} thigh`);
  } else if (kind === 'hair') {
    // ADR-039 T2a: no collider. The leaf swings about the skull's CENTRE inside a
    // 6° cone, so the hair it carries slides around the skull, never into it, and
    // the share is 0 at the scalp and where the hair rests on the body.
  } else {
    // A glute rides ON its own thigh: that capsule kicked the tip 36–44° every
    // stride of the Mixamo walk (the thigh swinging back into it — measured on
    // both tigers 2026-09-24), a job the skin's own thigh weights already do.
    // The OTHER thigh keeps a glute from swinging across the cleft.
    const other = leaf.region === 'LeftGlute' ? 'Right' : 'Left';
    capsuleFor(mapped(`${other}UpLeg`), mapped(`${other}Leg`), `the ${other.toLowerCase()} thigh`);
    {
      const twin = input.others.find((o) => o.region === (leaf.region === 'LeftGlute' ? 'RightGlute' : 'LeftGlute'));
      if (twin !== undefined) otherTipSphere(twin, 'the other glute');
    }
  }
  const joint = (bone: string) => ({
    bone: fileName(bone),
    hitRadius,
    stiffness: softTissueJointStiffness(controls),
    gravityPower: controls.gravity,
    gravityDir: [0, -1, 0] as [number, number, number],
    dragForce: controls.drag,
  });
  const decl = springDeclarationSchema.parse({
    name: softTissueSpringName(leaf.region),
    joints: [joint(leaf.bone), joint(leaf.tip)],
    colliders,
    provenance: { rule: `soft-tissue-prior@1:${kind}`, knobs: { stiffness: controls.stiffness, drag: controls.drag, gravity: controls.gravity } },
  });
  return { decl, extras: softTissueExtras(leaf.region, controls), fitted, hitRadius };
}

/** The same spring with new controls: live numbers rewritten, colliders and radii kept. */
export function retuneSoftTissueSpring(decl: SpringDeclaration, controls: SoftTissueControls, confirmedBy?: string): SpringDeclaration {
  const c = softTissueControlsSchema.parse(controls);
  return springDeclarationSchema.parse({
    ...decl,
    joints: decl.joints.map((j) => ({ ...j, stiffness: softTissueJointStiffness(c), dragForce: c.drag, gravityPower: c.gravity })),
    provenance: {
      ...decl.provenance,
      knobs: { stiffness: c.stiffness, drag: c.drag, gravity: c.gravity },
      ...(confirmedBy !== undefined ? { confirmedBy } : {}),
    },
  });
}

/** The leaf slot names a region grafts, as the FILE speaks them. */
export const softTissueFileBones = (region: JiggleRegionId): { bone: string; tip: string } => ({
  bone: SOFT_TISSUE_SLOT[region],
  tip: `${SOFT_TISSUE_SLOT[region]}_End`,
});

export interface LimbRadiusInput {
  readonly positions: ArrayLike<number>;
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> };
  /** Skin-order bones with parents and bind-world positions. */
  readonly bones: readonly { readonly name: string; readonly parent: string | null; readonly worldPosition: Vec3Data }[];
  /** The segment's upper bone (the thigh, the upper arm) and its lower joint (the knee, the elbow). */
  readonly upper: string;
  readonly lower: string;
}

/** Share of the median flesh distance a limb capsule takes — inside the flesh, not around it (the tail colliders' rule). */
export const LIMB_FLESH_SHARE = 0.9;

/**
 * A limb segment's flesh radius: the median distance to the segment of the
 * vertices the upper bone — or a bone under it that is not the lower joint's
 * subtree (a twist helper) — dominates, × {@link LIMB_FLESH_SHARE}. Undefined
 * when the segment owns no flesh.
 */
export function limbFleshRadius(input: LimbRadiusInput): number | undefined {
  const byName = new Map(input.bones.map((b) => [b.name, b]));
  const up = byName.get(input.upper);
  const lo = byName.get(input.lower);
  if (up === undefined || lo === undefined) return undefined;
  const inSegment = input.bones.map((b) => {
    let at: (typeof input.bones)[number] | undefined = b;
    while (at !== undefined) {
      if (at.name === input.lower) return false;
      if (at.name === input.upper) return true;
      at = at.parent === null ? undefined : byName.get(at.parent);
    }
    return false;
  });
  const d: number[] = [];
  const n = Math.floor(input.positions.length / 3);
  for (let v = 0; v < n; v++) {
    let best = -1;
    let bw = -1;
    for (let k = 0; k < 4; k++) {
      const w = input.skin.weight[v * 4 + k]!;
      if (w > bw) {
        bw = w;
        best = input.skin.index[v * 4 + k]!;
      }
    }
    if (!inSegment[best]) continue;
    const p = { x: input.positions[v * 3]!, y: input.positions[v * 3 + 1]!, z: input.positions[v * 3 + 2]! };
    d.push(distanceToSegment(p, up.worldPosition, lo.worldPosition));
  }
  if (d.length === 0) return undefined;
  d.sort((a, b) => a - b);
  return d[Math.floor(d.length / 2)]! * LIMB_FLESH_SHARE;
}
